import React, { useState, useCallback } from "react";
import {
  SafeAreaView,
  ScrollView,
  Text,
  View,
  ActivityIndicator,
  TouchableOpacity,
  RefreshControl,
} from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import { Colors } from "@/constants/Colors";
import Header from "@/components/ui/Header";
import { FontFamilies } from "@/constants/Typography";
import useUserStore from "@/store/user";
import { router } from "expo-router";
import StripePaymentServices, {
  StripePaymentMethod,
  StripeInvoice,
} from "@/services/stripePaymentServices";
import { showError, showSuccess, showConfirm } from "@/utils/toast";
import ContactSupportBottomSheet from "@/components/ContactSupportBottomSheet";
import {
  SubscriptionInfoCard,
  PaymentMethodsSection,
  InvoiceHistorySection,
} from "@/components/payment";
import BottomSheet from "@/components/BottomSheet";
import { ActionButton } from "@/components/ActionButton";
import {StyleSheet} from "@/utils/appStyleSheet";

const PaymentScreen = () => {
  const { userProfile, isFreePlan } = useUserStore();
  const plan = userProfile?.plan;

  const [paymentMethods, setPaymentMethods] = useState<StripePaymentMethod[]>(
    []
  );
  const [invoices, setInvoices] = useState<StripeInvoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showContactSupport, setShowContactSupport] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<StripePaymentMethod | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [paymentMethodsRes, invoicesRes] = await Promise.all([
        StripePaymentServices.getPaymentMethods(),
        StripePaymentServices.getInvoiceHistory(),
      ]);

      if (paymentMethodsRes.success) {
        setPaymentMethods(paymentMethodsRes.data.paymentMethods);
      }

      if (invoicesRes.success) {
        setInvoices(invoicesRes.data.invoices);
      }
    } catch (error) {
      console.error("Error fetching payment data:", error);
      showError("Failed to load payment information");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  const onRefresh = useCallback(() => {
    setIsRefreshing(true);
    fetchData();
  }, [fetchData]);

  const handleSetDefaultPaymentMethod = async (paymentMethodId: string) => {
    try {
      const res = await StripePaymentServices.setDefaultPaymentMethod(
        paymentMethodId
      );
      if (res.success) {
        showSuccess("Default payment method updated");
        setPaymentMethods((prev) =>
          prev.map((pm) => ({
            ...pm,
            isDefault: pm.id === paymentMethodId,
          }))
        );
      } else {
        showError("Failed to update default payment method");
      }
    } catch (error) {
      console.error("Error setting default payment method:", error);
      showError("Failed to update default payment method");
    }
    setSelectedPaymentMethod(null);
  };

  const handleDeletePaymentMethod = async (paymentMethodId: string) => {
    setSelectedPaymentMethod(null);

    const confirmed = await showConfirm({
      title: "Remove Payment Method",
      message: "Are you sure you want to remove this payment method?",
      confirmText: "Remove",
      cancelText: "Cancel",
    });

    if (confirmed) {
      try {
        const res = await StripePaymentServices.deletePaymentMethod(
          paymentMethodId
        );
        if (res.success) {
          showSuccess("Payment method removed");
          setPaymentMethods((prev) =>
            prev.filter((pm) => pm.id !== paymentMethodId)
          );
        } else {
          showError("Failed to remove payment method");
        }
      } catch (error) {
        console.error("Error deleting payment method:", error);
        showError("Failed to remove payment method");
      }
    }
  };

  const handleAddPaymentMethod = () => {
    router.push("/account/add-card" as any);
  };

  const handlePaymentMethodOptions = (paymentMethod: StripePaymentMethod) => {
    setSelectedPaymentMethod(paymentMethod);
  };

  const handleCloseOptionsSheet = () => {
    setSelectedPaymentMethod(null);
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <Header title="Payment" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={Colors.primary} />
          <Text style={styles.loadingText}>Loading payment information...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <Header title="Payment" />

      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollViewContent}
        refreshControl={
          <RefreshControl refreshing={isRefreshing} onRefresh={onRefresh} />
        }
      >
        {/* Subscription Info Section */}
        {!isFreePlan && plan && (
          <SubscriptionInfoCard
            plan={plan}
            onContactSupport={() => setShowContactSupport(true)}
          />
        )}

        {/* Payment Methods Section */}
        <PaymentMethodsSection
          paymentMethods={paymentMethods}
          onOptionsPress={handlePaymentMethodOptions}
          onAddPaymentMethod={handleAddPaymentMethod}
        />

        {/* Invoice History Section */}
        <InvoiceHistorySection invoices={invoices} />

        {/* Cancel Subscription Button */}
        {!isFreePlan && plan?.active && !plan?.cancelAtPeriodEnd && (
          <TouchableOpacity
            style={styles.cancelButton}
            onPress={() => router.push("/account/cancel")}
          >
            <Text style={styles.cancelButtonText}>Cancel subscription</Text>
          </TouchableOpacity>
        )}

        <View style={styles.bottomSpacer} />
      </ScrollView>

      {/* Contact Support Bottom Sheet */}
      <ContactSupportBottomSheet
        isVisible={showContactSupport}
        onClose={() => setShowContactSupport(false)}
      />

      {/* Payment Method Options Bottom Sheet */}
      <BottomSheet
        isVisible={!!selectedPaymentMethod}
        onClose={handleCloseOptionsSheet}
        height={200}
        enableBackdropDismiss
        enableSwipeDown
      >
        <View style={styles.optionsContainer}>
          <Text style={styles.optionsTitle}>Payment Method Options</Text>

          {selectedPaymentMethod && !selectedPaymentMethod.isDefault && (
            <ActionButton
              title="Set as Default"
              type="secondary"
              onPress={() =>
                handleSetDefaultPaymentMethod(selectedPaymentMethod.id)
              }
              style={styles.optionButton}
            />
          )}

          <ActionButton
            title="Remove"
            type="hide"
            onPress={() =>
              selectedPaymentMethod &&
              handleDeletePaymentMethod(selectedPaymentMethod.id)
            }
            style={styles.optionButton}
            textStyle={styles.removeButtonText}
          />
        </View>
      </BottomSheet>
    </SafeAreaView>
  );
};

export default PaymentScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: 16,
  },
  scrollViewContent: {
    paddingTop: 8,
    paddingBottom: 32,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: Colors.text,
  },
  cancelButton: {
    borderRadius: 12,
    paddingVertical: 16,
    alignSelf: "center",
  },
  cancelButtonText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#A3A3A3",
  },
  bottomSpacer: {
    height: 32,
  },
  optionsContainer: {
    paddingHorizontal: 0,
    gap: 12,
  },
  optionsTitle: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    textAlign: "center",
    marginBottom: 8,
    color: "#000",
  },
  optionButton: {
    width: "100%",
    height: 48,
  },
  removeButtonText: {
    color: "#C12915",
  },
});
